import React from "react";

import {
  FormGroup,
  InputGroup,
  TextArea,
  Switch,
  Card,
  MenuItem,
  Intent,
  Callout
} from "@blueprintjs/core";

import { MultiSelect } from "@blueprintjs/select";

import styles from "./ProductSidebarInfo.module.scss";
import { Image } from "../../atoms/Image";
import { Icon } from "../../atoms/Icon";
import { CurrencyInput } from "../../atoms/CurrencyInput";
import { ImageCropper } from "../../atoms/ImageCropper";
import { Spinner, Button } from "@blueprintjs/core";
import { useHistory } from "react-router-dom";

import classnames from "classnames";
import { FormInfo } from "../../atoms/FormInfo";

function ProductSidebarInfo({
  activeOpportunity,
  productImage,
  onProductImageSelect,
  isImageLoading,
  setIsImageLoading,
  hasVariants,
  onToggleHasVariants,
  acceptsDonations,
  onToggleAcceptsDonations,
  setActiveTab,
  name,
  onNameChange,
  description,
  onDescriptionChange,
  selectedCategories,
  selectableCategories,
  onSelectCategory,
  onUnselectCategory,
  onUnselectAllCategories,
  categoriesItemPredicate,
  categoryTagRenderer,
  price,
  onPriceChange,
  stock,
  onStockChange,
  sku,
  onSkuChange,
  isValidatingForms,
  fixedFee,
  variableFee,
  isPriceValid,
  ziplineFee
}) {
  const navigateToProductVariants = () => setActiveTab("ProductVariants");

  const history = useHistory();
  return (
    <div>
      <span className="contentTitle">Product Details</span>
      <div className={styles.productInfo}>
        {productImage && !isImageLoading ? (
          <div>
            <ImageCropper
              setLoading={setIsImageLoading}
              loading={isImageLoading}
              onImageSelect={onProductImageSelect}
            >
              <div className={styles.productImage}>
                <Image src={productImage} size={108} />
                <span>Edit Image(s)</span>
              </div>
            </ImageCropper>
          </div>
        ) : (
          <div>
            <ImageCropper
              setLoading={setIsImageLoading}
              loading={isImageLoading}
              onImageSelect={onProductImageSelect}
            >
              <div
                className={classnames(
                  styles.blankProductImage,
                  isValidatingForms &&
                    !isImageLoading &&
                    !productImage &&
                    styles.invalid
                )}
              >
                {isImageLoading ? (
                  <Spinner intent="success" size={22} />
                ) : (
                  <Icon name="camera" color="#9fa9ba" size={22} />
                )}
              </div>
            </ImageCropper>
          </div>
        )}

        <div className={styles.productInfoInputs}>
          <InputGroup
            placeholder="Name"
            className={classnames(isValidatingForms && !name && styles.invalid)}
            value={name}
            onChange={onNameChange}
            id="text-input"
            large
          />
          <TextArea
            placeholder="Description"
            value={description}
            onChange={onDescriptionChange}
            className={classnames(
              styles.description,
              isValidatingForms && !description && styles.invalid
            )}
            id="text-input"
            large
            fill
          />
          <FormInfo>Markdown is supported</FormInfo>

          <FormGroup label="Categories" labelFor="tag-input">
            <MultiSelect
              placeholder="Select Categories"
              large
              fill
              resetOnSelect
              activeItem={null}
              onItemSelect={onSelectCategory}
              tagRenderer={categoryTagRenderer}
              selectedItems={selectedCategories}
              items={selectableCategories}
              tagInputProps={{
                onRemove: onUnselectCategory,
                rightElement: !!selectedCategories.length && (
                  <Icon
                    name="cross"
                    size={16}
                    color="#2c2c2c"
                    className={styles.removeAllCategoriesIcon}
                    onClick={onUnselectAllCategories}
                  />
                ),
                large: true,
                tagProps: {
                  intent: "success",
                  minimal: true
                }
              }}
              itemPredicate={categoriesItemPredicate}
              itemRenderer={(category, { modifiers, handleClick }) =>
                !modifiers.matchesPredicate ? null : (
                  <MenuItem
                    active={modifiers.active}
                    icon={
                      !!selectedCategories.find(
                        (selectedCategory) =>
                          selectedCategory.id === category.id
                      ) ? (
                        <Icon name="tick" size={16} color="#00998c" />
                      ) : (
                        "blank"
                      )
                    }
                    key={category.id}
                    onClick={handleClick}
                    text={category.name}
                    shouldDismissPopover={false}
                  />
                )
              }
              popoverProps={{
                minimal: true,
                popoverClassName: styles.categoryDropdown
              }}
              noResults={<MenuItem disabled={true} text="No results." />}
            />
          </FormGroup>

          {activeOpportunity ? (
            <Callout
              title={
                "You cannot modify price or toggle on or off variants while an opportunity is active"
              }
              intent={Intent.WARNING}
              icon="error"
            >
              <Button
                className="filled"
                onClick={() => history.push("/opportunities")}
              >
                Go To Opportunities Page
              </Button>
            </Callout>
          ) : (
            <>
              <FormGroup helperText="Toggling on, if you are a non-profit organization seeking to raise donations instead of sales on goods, allows this product to become a donation.">
                <Switch
                  labelElement={
                    <span className={styles.hasVariantsLabel}>
                      Accepts Donations
                    </span>
                  }
                  large
                  className={styles.hasVariants}
                  checked={acceptsDonations}
                  onChange={onToggleAcceptsDonations}
                />
              </FormGroup>

              {!acceptsDonations && (
                <FormGroup helperText="Toggling on allows product variants to be added.">
                  <Switch
                    labelElement={
                      <span className={styles.hasVariantsLabel}>
                        Product has Variants
                      </span>
                    }
                    large
                    className={styles.hasVariants}
                    checked={hasVariants}
                    onChange={onToggleHasVariants}
                    disabled={acceptsDonations}
                  />
                </FormGroup>
              )}

              {!hasVariants && !acceptsDonations && (
                <>
                  <FormGroup
                    label="Price Details"
                    className={styles.priceDetails}
                    helperText={`Video Squirrel collects a ${(
                      variableFee * 100
                    ).toFixed(2)}% variable fee 
                        and a $${fixedFee} fixed fee per sale. This amount is deducted
                        from the product price prior to payment.`}
                    labelFor="text-input"
                  >
                    <CurrencyInput
                      placeholder="Product Price"
                      className={classnames(
                        isValidatingForms &&
                          !hasVariants &&
                          !price &&
                          styles.invalid
                      )}
                      value={price}
                      onChange={onPriceChange}
                      large
                    />
                    <CurrencyInput
                      placeholder="Fee Collection ($)"
                      value={ziplineFee}
                      large
                      disabled
                    />
                  </FormGroup>

                  <CurrencyInput
                    placeholder="Total Price ($)"
                    value={price && (price - ziplineFee).toFixed(2)}
                    large
                    disabled
                  />
                  {!isPriceValid && !!price && (
                    <Callout
                      icon="error"
                      intent={Intent.WARNING}
                      title={"Total price can not be negative."}
                    ></Callout>
                  )}
                </>
              )}
            </>
          )}

          {acceptsDonations && (
            <>
              <Card className={styles.hasVariantsInfoCard}>
                <Icon
                  name="info-sign"
                  className={styles.variantInfoIcon}
                  color="#8a9ba8"
                  size={15}
                />
                <span>
                  Your product accepts donations from customers instead of
                  having a fixed price.
                </span>
              </Card>
            </>
          )}

          {!hasVariants && (
            <>
              <FormGroup
                label="Other Details"
                className={styles.otherDetails}
                helperText="
                A SKU helps you identify your product on sales reports,
                even if the name of that product changes.
                "
                labelFor="text-input"
              >
                <InputGroup
                  id="text-input"
                  large
                  placeholder="Stock (Unlimited)"
                  value={stock}
                  onChange={onStockChange}
                />
                <InputGroup
                  id="text-input"
                  large
                  placeholder="SKU"
                  className={classnames(
                    isValidatingForms && !hasVariants && !sku && styles.invalid
                  )}
                  value={sku}
                  onChange={onSkuChange}
                />
              </FormGroup>
            </>
          )}

          {!!hasVariants && (
            <>
              <Card className={styles.hasVariantsInfoCard}>
                <Icon
                  name="info-sign"
                  className={styles.variantInfoIcon}
                  color="#8a9ba8"
                  size={15}
                />
                <span>
                  Price, stock, and SKU are specified per variant when adding
                  product variant detail information.
                </span>
              </Card>
              <Button
                rightIcon={<Icon name="chevron-right" />}
                onClick={navigateToProductVariants}
              >
                Manage Variants
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductSidebarInfo;
